import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const HERO_IMAGE = "https://images.unsplash.com/photo-1663811396612-b94cac5b0e33?w=1600&q=80";

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8 }
};

const TurnkeyPage = () => {
  return (
    <div className="min-h-screen bg-black pt-20" data-testid="turnkey-page">

      {/* 1. HERO */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE}
            alt="INTROZZI tailored bathroom"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <motion.div 
          className="relative z-10 text-center px-6 max-w-3xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight leading-tight">
            We are not for everyone.
          </h1>
          <motion.div 
            className="mx-auto mt-6 h-[1px] bg-[#C5A059]"
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ delay: 1, duration: 0.8 }}
          />
          <p className="mt-8 text-lg sm:text-xl text-gray-300 font-light italic font-['Playfair_Display']">
            And there is a reason.
          </p>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-[1px] h-12 bg-gradient-to-b from-white/0 via-white/40 to-white/0" />
        </motion.div>
      </section>

      {/* Hero Text Block */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24 bg-black">
        <motion.div className="max-w-3xl mx-auto text-center" {...fade}>
          <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9]">
            Our bathrooms follow one single principle:<br />
            every element is crafted by artisans, as much as possible through traditional and manual processes.
          </p>
          <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9] mt-8">
            In a world where everything is standardized, we choose the opposite path:<br />
            knowledge, materials, and the time required to do things properly.
          </p>
          <p className="text-white text-base sm:text-lg font-light leading-[1.9] mt-8">
            We are not for everyone, because the value of what we create<br />
            comes from the hands of those who make it.
          </p>
        </motion.div>
      </section>

      {/* 2. CONCEPT BLOCK */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A]">
        <motion.div className="max-w-4xl" {...fade}>
          <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] mb-6">
            Concept
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-10">
            A bathroom is not a product.
          </h2>
          <div className="max-w-2xl">
            <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9]">
              It is a bespoke project, built entirely around you.
            </p>
            <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9] mt-6">
              No compromises, no standard solutions, no shortcuts.
            </p>
            <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9] mt-6">
              Every detail is designed, selected, and crafted to last over time,
              both aesthetically and technically.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Gold separator */}
      <div className="flex justify-center py-4 bg-black">
        <div className="w-16 h-[1px] bg-[#C5A059]/40" />
      </div>

      {/* 3. SERVICE BLOCK */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24 bg-black">
        <motion.div className="max-w-4xl" {...fade}>
          <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] mb-6">
            Service
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-10">
            One point of contact. Zero stress.
          </h2>
          <div className="max-w-2xl">
            <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9] mb-10">
              We handle everything from start to finish:
            </p>
            <ul className="space-y-4">
              {[
                "Custom design",
                "Material selection",
                "Project management",
                "Coordination of our team of specialized artisans"
              ].map((item, i) => (
                <motion.li 
                  key={i}
                  className="flex items-start gap-4 text-gray-300 text-base font-light"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2.5 flex-shrink-0" />
                  {item}
                </motion.li>
              ))}
            </ul>
            <p className="text-white text-base sm:text-lg font-light leading-[1.9] mt-10">
              You deal with one single partner.<br />
              We take care of the rest.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 4. PROCESS BLOCK */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A]">
        <motion.div {...fade}>
          <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059] mb-6">
            Process
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-16 lg:mb-24">
            How we work
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16 lg:gap-y-20">
          {[
            {
              num: "01",
              title: "Contact",
              text: "Tell us about your vision and requirements."
            },
            {
              num: "02",
              title: "Site inspection",
              text: "We visit your space to understand its full potential."
            },
            {
              num: "03",
              title: "Proposal",
              text: "A tailored project designed around your needs."
            },
            {
              num: "04",
              title: "Construction begins",
              text: "Our team of specialized artisans starts the work."
            },
            {
              num: "05",
              title: "Project supervision",
              text: "Full oversight and quality control at every stage."
            },
            {
              num: "06",
              title: "Final result & handover",
              text: "Impeccable finishes, delivered on time."
            }
          ].map((item, i) => (
            <motion.div
              key={item.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
            >
              <span className="text-[#C5A059]/30 text-sm tracking-widest font-light">{item.num}</span>
              <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl text-white mt-3 mb-4">
                {item.title}
              </h3>
              <p className="text-gray-500 font-light leading-relaxed">
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. CLOSING BLOCK */}
      <section className="py-24 sm:py-32 lg:py-48 px-6 sm:px-12 lg:px-24 bg-black">
        <motion.div className="max-w-3xl mx-auto text-center" {...fade}>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-10">
            From vision to reality.
          </h2>
          <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9]">
            We transform your bathroom into a timeless space.
          </p>
          <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9] mt-8">
            If you are looking for the lowest price, we are not the right choice.
          </p>
          <p className="text-white text-base sm:text-lg font-light leading-[1.9] mt-4">
            If you are looking for something that truly lasts and holds value, then we are.
          </p>
        </motion.div>
      </section>

      {/* CONTACT SECTION */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A] border-t border-white/5">
        <motion.div className="max-w-2xl mx-auto text-center" {...fade}>
          <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9]">
            The first contact is handled exclusively via email.<br />
            This allows us to dedicate the right time to each request.
          </p>
          <p className="text-gray-400 text-base sm:text-lg font-light leading-[1.9] mt-8 mb-8">
            To begin your project, write directly to:
          </p>
          <a 
            href="mailto:contact@introzzi.ch"
            className="inline-block font-['Playfair_Display'] text-2xl sm:text-3xl text-white hover:text-[#C5A059] transition-colors duration-300"
          >
            contact@introzzi.ch
          </a>
          <div className="mt-6 flex justify-center">
            <div className="w-12 h-[1px] bg-[#C5A059]/40" />
          </div>
          <p className="text-gray-600 text-sm font-light mt-8 max-w-md mx-auto">
            We only take on a limited number of projects each month.
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 sm:px-12 lg:px-24 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <p className="text-gray-600 text-xs">&copy; 2022 INTROZZI. All rights reserved.</p>
            <p className="text-gray-700 text-[10px] mt-1">brand of idrotek &mdash; CHE-468.961.634</p>
          </div>
          <Link to="/" className="text-gray-600 hover:text-gray-400 text-xs transition-colors">
            introzzi.ch
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default TurnkeyPage;
